import React, { useRef, useState } from 'react';
import {
  Box,
  SimpleGrid,
  Image,
  Text,
  Badge,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  VStack,
  HStack,
  Button,
  Icon,
  Progress,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { FiUpload, FiCamera } from 'react-icons/fi';
import { format, parseISO, differenceInDays } from 'date-fns';
import {
  useChunkedUpload,
  type MediaCDNUploadResult,
} from '../../../../media-cdn/hooks/useChunkedUpload';
import {
  BUILD_STAGE_LABELS,
  BUILD_STAGE_ORDER,
  type BuildPhoto,
  type BuildStage,
} from '../types';

const PROPERTY_PRIMARY = '#0D7F6E';

interface BuildPhotoGalleryProps {
  photos: BuildPhoto[];
  currentUserIsPC?: boolean;
  onUpload?: (stage: BuildStage, fileUrl: string, caption: string) => void;
}

const isNew = (takenAt: string) => differenceInDays(new Date(), parseISO(takenAt)) <= 7;

export const BuildPhotoGallery: React.FC<BuildPhotoGalleryProps> = ({
  photos,
  currentUserIsPC = false,
  onUpload,
}) => {
  const toast = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [lightboxPhoto, setLightboxPhoto] = useState<BuildPhoto | null>(null);
  const [uploadStage, setUploadStage] = useState<BuildStage>('SLAB');
  const [uploadCaption, setUploadCaption] = useState('');

  const { uploadFile, isUploading, progress } = useChunkedUpload({
    chunkSize: 10 * 1024 * 1024,
    onComplete: (result: MediaCDNUploadResult) => {
      onUpload?.(uploadStage, result.url, uploadCaption || `${BUILD_STAGE_LABELS[uploadStage]} photo`);
      setUploadCaption('');
      toast({ title: 'Photo uploaded', status: 'success', duration: 3000 });
    },
    onError: () => {
      toast({ title: 'Upload failed — please try again', status: 'error', duration: 4000 });
    },
  });

  // Stages that have at least one photo
  const stagesWithPhotos = BUILD_STAGE_ORDER.filter(s => photos.some(p => p.stage === s));
  const allStages = stagesWithPhotos.length > 0 ? stagesWithPhotos : (['PLANNING'] as BuildStage[]);

  const openLightbox = (photo: BuildPhoto) => {
    setLightboxPhoto(photo);
    onOpen();
  };

  return (
    <Box>
      {/* Upload strip for PC role */}
      {currentUserIsPC && (
        <HStack
          bg="teal.50"
          border="1px solid"
          borderColor="teal.200"
          borderRadius="xl"
          p={3}
          mb={4}
          spacing={3}
          flexWrap="wrap"
        >
          <Icon as={FiCamera} color={PROPERTY_PRIMARY} boxSize={5} />
          <Text fontSize="sm" color="teal.700" flex={1}>
            Upload progress photos for clients
          </Text>
          <Button
            size="sm"
            bg={PROPERTY_PRIMARY}
            color="white"
            _hover={{ bg: '#0a6b5d' }}
            leftIcon={<FiUpload />}
            onClick={() => fileInputRef.current?.click()}
            isLoading={isUploading}
            loadingText="Uploading…"
          >
            Upload Photos
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            style={{ display: 'none' }}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) uploadFile(file);
            }}
          />
        </HStack>
      )}

      {isUploading && (
        <Box mb={3}>
          <Progress value={progress.percentComplete} size="xs" colorScheme="teal" borderRadius="full" />
          <Text fontSize="xs" color="gray.500" mt={1}>
            {Math.round(progress.percentComplete)}% uploaded
          </Text>
        </Box>
      )}

      {photos.length === 0 ? (
        <Box
          bg="gray.50"
          borderRadius="xl"
          p={10}
          textAlign="center"
          border="1px dashed"
          borderColor="gray.300"
        >
          <Icon as={FiCamera} boxSize={10} color="gray.300" mb={3} />
          <Text color="gray.400" fontSize="sm">
            Build photos will appear here as each stage progresses
          </Text>
        </Box>
      ) : (
        <Tabs variant="soft-rounded" colorScheme="teal" size="sm">
          <TabList mb={4} flexWrap="wrap" gap={2}>
            <Tab
              fontSize="xs"
              _selected={{ bg: PROPERTY_PRIMARY, color: 'white' }}
            >
              All
              <Badge ml={1.5} borderRadius="full" bg="teal.100" color="teal.700" fontSize="2xs">
                {photos.length}
              </Badge>
            </Tab>
            {allStages.map(stage => {
              const count = photos.filter(p => p.stage === stage).length;
              const hasNew = photos.filter(p => p.stage === stage).some(p => isNew(p.takenAt));
              return (
                <Tab key={stage} fontSize="xs" _selected={{ bg: PROPERTY_PRIMARY, color: 'white' }}>
                  {BUILD_STAGE_LABELS[stage]}
                  {hasNew && (
                    <Badge ml={1} borderRadius="full" bg="orange.400" color="white" fontSize="2xs">
                      New
                    </Badge>
                  )}
                  {!hasNew && (
                    <Badge ml={1.5} borderRadius="full" bg="gray.100" color="gray.500" fontSize="2xs">
                      {count}
                    </Badge>
                  )}
                </Tab>
              );
            })}
          </TabList>

          <TabPanels>
            {/* All photos */}
            <TabPanel p={0}>
              <PhotoGrid photos={photos} onPhotoClick={openLightbox} />
            </TabPanel>
            {/* Per-stage panels */}
            {allStages.map(stage => (
              <TabPanel key={stage} p={0}>
                <PhotoGrid
                  photos={photos.filter(p => p.stage === stage)}
                  onPhotoClick={openLightbox}
                />
              </TabPanel>
            ))}
          </TabPanels>
        </Tabs>
      )}

      {/* Lightbox */}
      <Modal isOpen={isOpen} onClose={onClose} size="3xl" isCentered>
        <ModalOverlay bg="blackAlpha.800" />
        <ModalContent bg="gray.900" borderRadius="xl" overflow="hidden">
          <ModalCloseButton color="white" zIndex={10} />
          <ModalBody p={0}>
            {lightboxPhoto && (
              <VStack spacing={0}>
                <Image
                  src={lightboxPhoto.fileUrl}
                  alt={lightboxPhoto.caption}
                  w="full"
                  maxH="70vh"
                  objectFit="contain"
                  bg="black"
                />
                <Box w="full" p={4}>
                  <HStack justify="space-between">
                    <VStack align="flex-start" spacing={0}>
                      <Text color="white" fontWeight="semibold" fontSize="sm">
                        {lightboxPhoto.caption}
                      </Text>
                      <Text color="gray.400" fontSize="xs">
                        {BUILD_STAGE_LABELS[lightboxPhoto.stage]} ·{' '}
                        {format(parseISO(lightboxPhoto.takenAt), 'd MMM yyyy')}
                      </Text>
                    </VStack>
                    {isNew(lightboxPhoto.takenAt) && (
                      <Badge colorScheme="orange" borderRadius="full" fontSize="xs">New</Badge>
                    )}
                  </HStack>
                </Box>
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

interface PhotoGridProps {
  photos: BuildPhoto[];
  onPhotoClick: (photo: BuildPhoto) => void;
}

const PhotoGrid: React.FC<PhotoGridProps> = ({ photos, onPhotoClick }) => (
  <SimpleGrid columns={{ base: 2, md: 3 }} spacing={3}>
    {photos
      .slice()
      .sort((a, b) => new Date(b.takenAt).getTime() - new Date(a.takenAt).getTime())
      .map(photo => (
        <Box
          key={photo.id}
          position="relative"
          borderRadius="lg"
          overflow="hidden"
          cursor="pointer"
          onClick={() => onPhotoClick(photo)}
          _hover={{ transform: 'scale(1.02)', boxShadow: 'lg' }}
          transition="all 0.15s"
          boxShadow="sm"
          bg="gray.100"
          aspectRatio="4/3"
        >
          <Image
            src={photo.thumbnailUrl}
            alt={photo.caption}
            w="full"
            h="full"
            objectFit="cover"
          />
          {isNew(photo.takenAt) && (
            <Badge
              position="absolute"
              top={2}
              right={2}
              colorScheme="orange"
              borderRadius="full"
              fontSize="2xs"
              px={2}
            >
              New
            </Badge>
          )}
          <Box
            position="absolute"
            bottom={0}
            left={0}
            right={0}
            bg="blackAlpha.600"
            px={2}
            py={1.5}
          >
            <Text
              color="white"
              fontSize="xs"
              noOfLines={1}
            >
              {photo.caption}
            </Text>
          </Box>
        </Box>
      ))}
  </SimpleGrid>
);
